// pages/myshop/main.jsx
import React, { useEffect, useRef, useState, useMemo } from "react";
import interact from "interactjs";
import { nanoid } from "nanoid";
import Swal from "sweetalert2";
import withReactContent from "sweetalert2-react-content";
import { getApiClient } from "../../libs/axiosInstance";
import { loadAuth, saveAuthByKey } from "../../libs/authMgmt";
import Wrapper from "../_Wrapper";
import * as Lucide from "lucide-react";
import { getSubdomain } from "../../libs/helper";
import { QRCodeSVG } from "qrcode.react";
import ReactDOMServer from "react-dom/server";

// Expanded status set
// vacant       = table clean / empty
// reserved     = guests sitting / browsing / not verified yet
// ordered      = first order placed (needs verification) [red/high alert]
// preparing    = verified legit; kitchen working
// served       = food delivered; waiting bill

const STATUS_BG = {
  vacant: "bg-success",
  reserved: "bg-warning",
  occupied_ordered: "bg-danger",
  occupied_preparing: "bg-info",
  occupied_served: "bg-secondary",
  occupied_ready_to_serve: "bg-primary",
  served: "bg-secondary",
};

const STATUS_BORDER = {
  vacant: "border-success",
  reserved: "border-warning",
  occupied_ordered: "border-danger",
  occupied_preparing_meal: "border-info",
  occupied_served: "border-secondary",
  occupied_ready_to_serve: "border-primary",
  served: "border-secondary",
};

// Human-readable text we show under table box
export const STATUS_HUMAN = {
  vacant: "Vacant",
  reserved: "Seated / Ordering",
  occupied_ordered: "Ordered (Verify)",
  occupied_preparing_meal: "Preparing",
  occupied_served: "Served",
  occupied_ready_to_serve: "Ready to Serve",
  served: "Occupied (Served)",
};

const STATUS_LABEL = [
  { key: "vacant", label: "Mark Vacant / Clear" },
  //{ key: "reserved", label: "Mark Reserved" },
  //{ key: "ordered", label: "Mark Ordered (Unverified)" },
  { key: "occupied_preparing_meal", label: "Verify Order & Set to Preparing" },
  { key: "served", label: "Mark Served" },
];
function shortId(str) {
  if (!str) return "";
  // show last 4 chars para mabilis i-check visually
  return String(str).slice(-4);
}



export default function MyShop() {
  const axios = getApiClient();
  const authCompany = loadAuth("company") || {};
  const subdomain = loadAuth("subdomain");
  const myStoreEnabled = !!authCompany.myStoreEnabled;

  const [selectedMenu, setSelectedMenu] = useState(null);
  const [eMenus, setEMenus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [layout, setLayout] = useState(null);
  const [mode, setMode] = useState("live"); // 'design' | 'live'
  const [showGrid, setShowGrid] = useState(true);
  const [darkMode, setDarkMode] = useState(localStorage.getItem("plot_darkmode") === "true");
  const [contextMenu, setContextMenu] = useState(null); // { id, left, top }
  const boardRef = useRef(null);
  const boardWrapRef = useRef(null); // container for container-only fullscreen
  const ctxRef = useRef(null);       // context menu container ref

  const [designMode, setDesignMode] = useState(false);


  // SweetAlert helper that pins popups inside the fullscreen container when active
  const swal = (opts) =>
    Swal.fire({
      target: boardWrapRef.current || document.body,
      backdrop: true,
      ...opts,
    });
    
  // Use the container-aware swal helper for MySwal
  const MySwal = {
    fire: (opts) => swal(opts),
  };

  // escape HTML for swal
  const escapeHtml = (s) => {
    if (s == null) return "";
    return String(s)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;");
  };

  // GET /api/myshop/session-items?tableId=...
  async function handleSeeOrderedItems(id) {
    const el = layout.elements.find(x=>x.id === id);
    
    if (!el || !(el.sessionId || el.orderRef)) {
      MySwal.fire("No Active Order", "No session for this table.", "info");
      return;
    }

    try {
      const res = await axios.get("/api/myshop/session-items", {
        params: { tableId: el.id }, // we stick to el.id == elementId
      });

      const data = res.data || {};
      const rowsHtml = (data.items || [])  
        .map(
          (it) => `
          <tr>
            <td style="padding:4px 8px;">${escapeHtml(it.name || "")}</td>
            <td style="padding:4px 8px;text-align:right;">${it.qty}</td>
            <td style="padding:4px 8px;text-align:right;">
              ${
                it.unitPrice?.toFixed
                  ? it.unitPrice.toFixed(2)
                  : Number(it.unitPrice || 0).toFixed(2)
              }
            </td>
          </tr>`
        )
        .join("");

      const html = `
        <div style="text-align:left;">
          <div style="font-weight:600; margin-bottom:6px;">
            ${escapeHtml(data.tableLabel || el.label || "")}
            <div style="font-size:12px; font-weight:400; color:#666;">
              Status: ${escapeHtml(data.status || el.status || "")}
            </div>
          </div>
          <table style="width:100%; font-size:14px; border-collapse:collapse;">
            <thead>
              <tr style="text-align:left; background:#f5f5f5;">
                <th style="padding:4px 8px;">Item</th>
                <th style="padding:4px 8px;text-align:right;">Qty</th>
                <th style="padding:4px 8px;text-align:right;">Price</th>
              </tr>
            </thead>
            <tbody>${
              rowsHtml ||
              `<tr><td colspan="3" style="padding:8px;">No items.</td></tr>`
            }</tbody>
          </table>
        </div>
      `;

      await MySwal.fire({
        title: "Ordered Items",
        html,
        confirmButtonText: "Close",
        width: 400,
      });
    } catch (err) {
      console.error(err);
      MySwal.fire("Error", "Can't load ordered items.", "error");
    }
  }

  // show printable QR
  async function handleShowQR(id, menuId) {
    const el = layout.elements.find(x=>x.id === id);
    
    if (!el) return;

    // ⬇️ replace this with however you build the tenant URL in your app
    const tenantBaseUrl = window.location.origin;//getSubdomain(false);

    const orderUrl = `${tenantBaseUrl}/order/${el.id}/${menuId}`;

    // Generate inline SVG string
    const svgString = ReactDOMServer.renderToStaticMarkup(
      <QRCodeSVG
        value={orderUrl}
        size={220}
        bgColor="transparent"
        fgColor={document.body.classList.contains("dark-mode") ? "#fff" : "#000"}
        level="M"
        includeMargin={true}
      />
    );

    const html = `
      <div style="text-align:center;">
        <div style="font-size:14px; margin-bottom:8px;">
          ${escapeHtml(el.label || "")}
        </div>`+
        svgString
        +`
        <div style="font-size:12px; margin-top:8px; word-break:break-all;">
          <a target="_blank" href="${escapeHtml(orderUrl)}">${escapeHtml(orderUrl)}</a>
        </div>
      </div>
    `;

    await MySwal.fire({
      title: "Table QR",
      html,
      showCancelButton: true,
      cancelButtonText: "Close",
      confirmButtonText: "Print",
      preConfirm: () => {
        const win = window.open("", "printqr", "width=400,height=480");
        if (!win) return;
        win.document.write(`
          <html>
            <head>
              <title>${escapeHtml(el.label || "Table QR")}</title>
              <style>
                *{font-family:sans-serif;text-align:center;margin:0;padding:0}
                .wrap{padding:12px}
              </style>
            </head>
            <body>
              <div class="wrap">
                <div style="font-size:16px;font-weight:600;margin-bottom:8px;">
                  ${escapeHtml(el.label || "")}
                </div>
                `+
                svgString
                +
                `
                <div style="font-size:12px;margin-top:8px;word-break:break-all;">
                  ${escapeHtml(orderUrl)}
                </div>
              </div>
              <script>
                window.print();
                setTimeout(()=>window.close(),250);
              </script>
            </body>
          </html>
        `);
        win.document.close();
      },
    });
  }

  // SweetAlert helper that pins popups inside the fullscreen container when active
  // const swal = (opts) =>
  //   Swal.fire({
  //     target: boardWrapRef.current || document.body,
  //     backdrop: true,
  //     ...opts,
  //   });

  // ==== Derived
  const cellSize = 40; // universal size (hidden from UI)
  const snapStep = layout?.snapStep ?? Math.max(4, Math.floor(cellSize / 2));
  const snapToGrid = !!layout?.snapToGrid;


  useEffect(() => {
    let timer;
    let isDestroyed = false;
  
    async function poll() {
      try {
        // don't poll while designing to avoid "it blanks it out"
        if (mode === "design") {
          schedule();
          return;
        }
  
        const res = await axios.get("/api/myshop/layout"); // <- your existing endpoint
        const fresh = res.data?.layout || res.data;        // adjust to your shape
  
        // OPTIONAL: only update if changed (to avoid re-renders)
        setLayout((prev) => {
          if (!prev) return fresh;
          // naive compare by updatedAt / version / length
          if (prev.updatedAt !== fresh.updatedAt) return fresh;
          if ((prev.elements || []).length !== (fresh.elements || []).length)
            return fresh;
          return prev; // no change
        });
      } catch (err) {
        console.warn("myshop poll failed", err?.message);
      } finally {
        schedule();
      }
    }
  
    function schedule() {
      if (isDestroyed) return;
      timer = setTimeout(poll, 12000);
    }
  
    schedule();
  
    return () => {
      isDestroyed = true;
      if (timer) clearTimeout(timer);
    };
  }, [mode]);
  

  useEffect(()=>{
    localStorage.setItem("plot_darkmode", darkMode ? "true" : "false");
  }, [darkMode])

  // ===== LOAD / CREATE LAYOUT =====
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get("/api/myshop/layout", { headers: { "X-Tenant": subdomain } });
        const normalized = {
          ...res.data,
          grid: { ...(res.data?.grid || {}), cellSize: 40 },
          snapStep: res.data?.snapStep ?? Math.max(4, Math.floor(40 / 2)),
        };
        setLayout(normalized);
        setLoading(false);
      } catch (err) {
        if (err?.response?.status === 404) {
          const boardW = boardWrapRef.current?.clientWidth || 1200;
          const boardH = boardWrapRef.current?.clientHeight || 600;
          const cols = Math.max(3, Math.floor(boardW / 40));
          const rows = Math.max(3, Math.floor(boardH / 40));

          try {
            const saveRes = await axios.put(
              "/api/myshop/layout",
              {
                name: "Main Floor",
                grid: { cols, rows, cellSize: 40 },
                snapToGrid: true,
                snapStep: Math.max(4, Math.floor(40 / 2)),
                elements: [],
                fitToContainer: true,
              },
              { headers: { "X-Tenant": subdomain } }
            );
            setLayout(saveRes.data);
            setLoading(false);
            await swal({ icon: "success", title: "Created", text: "Your layout is ready." });
          } catch (e) {
            setLoading(false);
            await swal({ icon: "error", title: "Error", text: "Failed creating layout." });
          }
          return;
        }
        setLoading(false);
        await swal({ icon: "error", title: "Error", text: err?.message || "Failed to load." });
      }

      //get emenus
      try {
        const res2 = await axios.get("/api/myshop/menus");
        setEMenus(res2.data.menus);
      }catch{}

    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ===== DRAG/RESIZE with finer snap =====
  useEffect(() => {
    if (!boardRef.current || !layout || mode !== "design") return;

    const snapEnabled = !!layout.snapToGrid;
    const dragSnap = interact.snappers.grid({ x: snapStep, y: snapStep });
    const sizeSnap = interact.snappers.grid({ x: snapStep, y: snapStep });

    interact(".draggable")
      .draggable({
        listeners: {
          move(event) {
            const target = event.target;
            const x = (parseFloat(target.getAttribute("data-x")) || 0) + event.dx;
            const y = (parseFloat(target.getAttribute("data-y")) || 0) + event.dy;
            target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute("data-x", x);
            target.setAttribute("data-y", y);
          },
          end(event) {
            const id = event.target.dataset.id;
            const x = parseFloat(event.target.getAttribute("data-x")) || 0;
            const y = parseFloat(event.target.getAttribute("data-y")) || 0;
            setLayout(prev => {
              const next = { ...prev, elements: prev.elements.map(e => ({ ...e })) };
              const el = next.elements.find(e => e.id === id);
              if (el) {
                const snappedX = snapEnabled ? Math.round(x / snapStep) * snapStep : x;
                const snappedY = snapEnabled ? Math.round(y / snapStep) * snapStep : y;
                el.x = Math.max(0, snappedX);
                el.y = Math.max(0, snappedY);
              }
              return next;
            });
          },
        },
        modifiers: snapEnabled ? [interact.modifiers.snap({ targets: [dragSnap] })] : [],
      });

    interact(".resizable")
      .resizable({
        edges: { left: true, right: true, bottom: true, top: true },
        listeners: {
          move(event) {
            const target = event.target;
            let x = (parseFloat(target.getAttribute("data-x")) || 0);
            let y = (parseFloat(target.getAttribute("data-y")) || 0);
            target.style.width = `${event.rect.width}px`;
            target.style.height = `${event.rect.height}px`;
            x += event.deltaRect.left;
            y += event.deltaRect.top;
            target.style.transform = `translate(${x}px, ${y}px)`;
            target.setAttribute("data-x", x);
            target.setAttribute("data-y", y);
          },
          end(event) {
            const id = event.target.dataset.id;
            const w = event.rect.width;
            const h = event.rect.height;
            const x = parseFloat(event.target.getAttribute("data-x")) || 0;
            const y = parseFloat(event.target.getAttribute("data-y")) || 0;
            setLayout(prev => {
              const next = { ...prev, elements: prev.elements.map(e => ({ ...e })) };
              const el = next.elements.find(e => e.id === id);
              if (el) {
                el.w = (snapEnabled ? Math.round(w / snapStep) * snapStep : w);
                el.h = (snapEnabled ? Math.round(h / snapStep) * snapStep : h);
                el.x = (snapEnabled ? Math.round(x / snapStep) * snapStep : x);
                el.y = (snapEnabled ? Math.round(y / snapStep) * snapStep : y);
              }
              return next;
            });
          }
        },
        modifiers: [
          interact.modifiers.restrictSize({ min: { width: 24, height: 24 } }),
          ...(snapEnabled ? [interact.modifiers.snapSize({ targets: [sizeSnap] })] : []),
        ],
      });

    return () => {
      interact(".draggable").unset();
      interact(".resizable").unset();
    };
  }, [layout, mode, snapStep, snapToGrid]);

  // ===== helpers
  const addTable = async () => {
    setLayout(prev => ({
      ...prev,
      elements: [
        ...prev.elements,
        {
          id: nanoid(),
          type: "table",
          x: 100, y: 100, w: cellSize * 3, h: cellSize * 2,
          label: `Table ${prev.elements.filter(e => e.type === "table").length + 1}`,
          seatCount: 4,
          status: "vacant",
          orderRef: ""
        }
      ]
    }));
  };
  const addFixture = async () => {
    const { value: formValues } = await swal({
      title: "Add Fixture",
      html: `
        <div class="text-start">
          <div class="mb-2">
            <label class="form-label">Fixture Name</label>
            <input id="sw-fix-name" class="form-control" placeholder="e.g. Cashier Counter" />
          </div>
          <div class="row g-2">
            <div class="col">
              <label class="form-label">Width (px)</label>
              <input id="sw-fix-w" type="number" min="24" max="2000" class="form-control" value="80" />
            </div>
            <div class="col">
              <label class="form-label">Height (px)</label>
              <input id="sw-fix-h" type="number" min="24" max="2000" class="form-control" value="80" />
            </div>
          </div>
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Add",
      preConfirm: () => {
        const name = document.getElementById("sw-fix-name")?.value?.trim();
        const w = parseInt(document.getElementById("sw-fix-w")?.value || "0", 10);
        const h = parseInt(document.getElementById("sw-fix-h")?.value || "0", 10);
        if (!name) {
          Swal.showValidationMessage("Please enter a fixture name.");
          return;
        }
        if (!w || !h || w < 24 || h < 24) {
          Swal.showValidationMessage("Enter valid width and height (≥ 24px).");
          return;
        }
        return { name, w, h };
      },
    });

    if (!formValues) return; // canceled
    const { name, w, h } = formValues;

    setLayout((prev) => ({
      ...prev,
      elements: [
        ...prev.elements,
        {
          id: nanoid(),
          type: "fixture",
          x: 100,
          y: 100,
          w,
          h,
          label: name,
        },
      ],
    }));
  };


  const saveLayout = async () => {
    try {
      const res = await axios.put("/api/myshop/layout",
        { ...layout, grid: { ...(layout?.grid || {}), cellSize: 40 } },
        { headers: { "X-Tenant": subdomain } }
      );
      setLayout(res.data);
      await swal({ icon: "success", title: "Saved", text: "Layout updated." });
    } catch (e) {
      await swal({ icon: "error", title: "Error", text: e?.response?.data?.error || e.message || "Failed to save." });
    }
  };

  const toggleSnap = async () => setLayout(prev => ({ ...prev, snapToGrid: !prev.snapToGrid }));
  const toggleFineSnap = async () =>
    setLayout(prev => ({ ...prev, snapStep: prev.snapStep === cellSize ? Math.max(4, Math.floor(cellSize / 2)) : cellSize }));

  const setOrderRef = async (id, current) => {
    const { value } = await swal({
      title: "Link Order / Ticket…",
      html: `
        <div class="text-start">
          <label class="form-label">Order / Ticket # or Name</label>
          <input id="sw-order" class="form-control" value="${(current || "").replace(/"/g, "&quot;")}" />
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Apply",
      preConfirm: () => (document.getElementById("sw-order")?.value?.trim() || "")
    });
    if (value === undefined) return;
    const val = value;
    setLayout(prev => {
      const next = { ...prev, elements: prev.elements.map(e => ({ ...e })) };
      const el = next.elements.find(e => e.id === id && e.type === "table");
      if (el) el.orderRef = val;
      return next;
    });
    if (mode === "live") {
      try { await axios.patch("/api/myshop/seat", { elementId: id, orderRef: val }, { headers: { "X-Tenant": subdomain } }); } catch {}
    }
  };

  const editSeatCount = async (id, current = 4) => {
    const { value } = await swal({
      title: "Change seat count",
      html: `
        <div class="text-start">
          <label class="form-label">Seats</label>
          <input id="sw-seats" type="number" min="1" max="20" class="form-control" value="${current}" />
        </div>
      `,
      focusConfirm: false,
      showCancelButton: true,
      confirmButtonText: "Apply",
      preConfirm: () => {
        const v = parseInt(document.getElementById("sw-seats")?.value || "0", 10);
        if (!v || v < 1) { Swal.showValidationMessage("Enter a valid seat count."); return; }
        return v;
      },
    });
    if (value === undefined) return;
    setLayout(prev => {
      const next = { ...prev, elements: prev.elements.map(e => ({ ...e })) };
      const el = next.elements.find(e => e.id === id && e.type === "table");
      if (el) el.seatCount = value;
      return next;
    });
  };

 const editSize = async (id, currentW, currentH) => {
  // open resize dialog inside fullscreen using swal()
  const { value } = await swal({
    title: "Resize element",
    html: `
      <div class="text-start">
        <div class="mb-2">
          <label class="form-label">Width (px)</label>
          <input id="sw-w" type="number" min="24" max="2000" class="form-control" value="${currentW}" />
        </div>
        <div class="mb-2">
          <label class="form-label">Height (px)</label>
          <input id="sw-h" type="number" min="24" max="2000" class="form-control" value="${currentH}" />
        </div>
      </div>
    `,
    focusConfirm: false,
    showCancelButton: true,
    confirmButtonText: "Apply",
    preConfirm: () => {
      const w = parseInt(document.getElementById("sw-w")?.value || "0", 10);
      const h = parseInt(document.getElementById("sw-h")?.value || "0", 10);
      if (!w || !h || w < 24 || h < 24) {
        Swal.showValidationMessage("Enter valid width and height (≥ 24 px).");
        return;
      }
      return { w, h };
    },
  });
  if (!value) return;

  setLayout((prev) => {
    const next = { ...prev, elements: prev.elements.map((e) => ({ ...e })) };
    const el = next.elements.find((e) => e.id === id);
    if (el) {
      el.w = value.w;
      el.h = value.h;
    }
    return next;
  });
};

  const editLabel = async (id, current) => {
    const { value } = await swal({
      title: "Edit label",
      input: "text",
      inputValue: current || "",
      showCancelButton: true,
      confirmButtonText: "Apply",
    });
    if (value === undefined) return;
    setLayout(prev => {
      const next = { ...prev, elements: prev.elements.map(e => ({ ...e })) };
      const el = next.elements.find(e => e.id === id);
      if (el) el.label = value;
      return next;
    });
  };

  const changeStatus = async (id, status) => {
    let p = layout;
    setLayout(prev => {
      const next = { ...prev, elements: prev.elements.map(e => ({ ...e })) };
      const el = next.elements.find(e => e.id === id && e.type === "table");
      if (el){
        el.status = status;
        if(status === "vacant"){
          //also clear the order labels
          el.orderRef = "";
        }
      }
      return next;
    });
    try {
      await axios.patch("/api/myshop/seat", { elementId: id, status }, { headers: { "X-Tenant": subdomain } });
    } catch(err) {
      Swal.fire("Error", err.response?.data?.error, "error");
      setLayout(p);
    }
  };
  async function forceVacant(elementId) {
    try {
      const res = await axios.patch("/api/myshop/seat", {
        elementId,
        status: "vacant",
        forceClose: true,
      });
      // same way you refresh layout after normal changeStatus
      if (res.data) {
        setLayout(res.data);
      }
    } catch (err) {
      console.error(err);
      MySwal.fire("Error", "Failed to clear table.", "error");
    }
  }
  
  const removeElement = async (id) => {
    setLayout(prev => ({ ...prev, elements: prev.elements.filter(e => e.id !== id) }));
  };

  // ===== Fullscreen (container-only)
  const enterFullscreen = () => {
    const el = boardWrapRef.current;
    if (!el) return;
    if (!document.fullscreenElement) el.requestFullscreen?.();
    else document.exitFullscreen?.();
  };

  // ===== Context menu helpers
  const openContextMenu = (e, id) => {
    e.preventDefault();
    const container = boardRef.current;
    if (!container) return;

    const rect = container.getBoundingClientRect();
    let left = e.clientX - rect.left;
    let top = e.clientY - rect.top;

    const maxLeft = rect.width - 260 - 8;
    const maxTop = rect.height - 240;
    left = Math.max(8, Math.min(left, maxLeft));
    top = Math.max(8, Math.min(top, maxTop));

    setContextMenu({ id, left, top });
  };

  // Close menu only when clicking *outside* the context menu
  const onBoardMouseDown = (e) => {
    if (e.button !== 0) return; // only left click
    if (ctxRef.current && ctxRef.current.contains(e.target)) return;
    setContextMenu(null);
  };

  const boardBg = useMemo(() => {
    if (!showGrid) return darkMode ? "#0f0f12" : "#f8f9fb";
    const lineColor = darkMode ? "rgba(255,255,255,.08)" : "rgba(0,0,0,.05)";
    return `linear-gradient(to right, ${lineColor} 1px, transparent 1px), linear-gradient(to bottom, ${lineColor} 1px, transparent 1px)`;
  }, [darkMode, showGrid]);

  if (loading || !layout) return <Wrapper title="My Shop"><div className="p-4">Loading…</div></Wrapper>;

  const GridBoard = () => (
    <div
      ref={boardRef}
      className={`position-relative w-100 h-100 ${darkMode ? "bg-dark text-white" : "bg-white text-dark"}`}
      style={{
        backgroundImage: boardBg,
        backgroundSize: showGrid ? `${cellSize}px ${cellSize}px` : undefined,
        overflow: "hidden",
        userSelect: "none"
      }}
      onContextMenu={(e) => e.preventDefault()}
      onMouseDown={onBoardMouseDown}
    >
      {/* Header */}
      <div
  title={
    mode === "design"
      ? `Design Mode — Use this mode to arrange your shop layout.
    Drag and resize tables or fixtures, rename them, and adjust seat counts.
    Customer actions and table reservations are disabled here.`
          : `Live Mode — For daily operations.
    Moving or editing items is locked to prevent accidental layout changes.
    You can update table status, and manually link orders.`
      }
      className={`position-absolute px-3 py-2 text-light fw-semibold d-flex align-items-center gap-2 shadow-sm`}
      style={{
        cursor: "help",
        borderRadius: 20,
        top: 10,
        left: 10,
        backgroundColor: mode === "design" ? "#0d6efd" : "#198754", // Bootstrap blue/green
        zIndex: 1500,
      }}
    >
      You are currently in{" "}
      {mode === "design" ? (
        <b className="d-flex align-items-center gap-1">
          <Lucide.PencilRuler size={16} /> Design Mode
        </b>
      ) : (
        <b className="d-flex align-items-center gap-1">
          <Lucide.PlayCircle size={16} /> Live Mode
        </b>
      )}
    </div>


      {layout.elements.map((el) => {
        const isTable = el.type === "table";
        const isFixture = el.type === "fixture";
        return (
          <div
            key={el.id}
            data-id={el.id}
            className={`position-absolute draggable resizable ${isTable ? "rounded " + STATUS_BORDER[el.status] : "border-secondary"} border`}
            style={{
              width: el.w, height: el.h,
              transform: `translate(${el.x}px, ${el.y}px)`,
              background: isTable ? (darkMode ? "#1b1e24" : "white") : "transparent",
              color: darkMode ? "white" : "inherit",
              boxShadow: darkMode ? "0 1px 2px rgba(0,0,0,.3)" : "0 1px 2px rgba(0,0,0,.05)"
            }}
            data-x={el.x} data-y={el.y}
            onMouseDown={(e) => { if (e.button === 2) openContextMenu(e, el.id); }}
          >
            {isTable && (
              // <div style={{ border: "5px solid" }} className={`${STATUS_BG[el.status]} ${darkMode ? "bg-opacity-35" : "bg-opacity-25"} w-100 h-100 d-flex flex-column align-items-center justify-content-center`}>
              //   <div className="fw-semibold" style={{ lineHeight: 1 }}>{el.label || "Table"}</div>
              //   <div className={`${darkMode ? "text-light" : "text-muted"}`} style={{ fontSize: 12 }}>
              //     Seats: {el.seatCount ?? 4}
              //     {el.orderRef ? <> · <span title="Order / Ticket">{el.orderRef}</span></> : null}
              //   </div>
              // </div>
            <div className={`${STATUS_BG[el.status]} ${darkMode ? "bg-opacity-35" : "bg-opacity-25"} w-100 h-100 d-flex flex-column align-items-center justify-content-center`}>{el.label || "Table"}
              <div style={{ fontSize: "10px", lineHeight: 1.1 }}>
                Seats: {el.seatCount || "?"}
              </div>

              <div style={{ fontSize: "10px", lineHeight: 1.1 }}>
                {STATUS_HUMAN[el.status] || el.status}
              </div>
{/* el.sessionId ||  */}
              {(el.orderRef) && (
                <div
                  style={{
                    fontSize: "9px",
                    lineHeight: 1.1,
                    opacity: 0.8,
                  }}
                >
                  Order #{el.orderRef}
                </div>
              )}
              </div>
            )}
            {isFixture && (
              <div
                className={`text-center w-100 h-100 d-flex flex-column align-items-center justify-content-center ${
                  darkMode ? "text-light" : "text-secondary"
                }`}
                style={{
                  borderRadius: 6,
                  background: darkMode ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.03)",
                  border: darkMode ? "1px solid rgba(255,255,255,0.1)" : "1px solid rgba(0,0,0,0.1)",
                  boxShadow: darkMode
                    ? "inset 0 0 3px rgba(255,255,255,0.05)"
                    : "inset 0 0 3px rgba(0,0,0,0.1)",
                }}
              >
                <Lucide.Package size={14} />
                <div style={{ fontSize: 12 }}>{el.label || "Fixture"}</div>
              </div>

            )}
          </div>
        );
      })}

      {/* Context Menu */}
      {contextMenu && (() => {
        const el = layout.elements.find(e => e.id === contextMenu.id);
        if (!el) return null;
        const isTable = el.type === "table";
        return (
          <div
            ref={ctxRef}
            className={`position-absolute card shadow ${darkMode ? "bg-dark text-white border-secondary" : ""}`}
            style={{
              left: contextMenu.left, top: contextMenu.top, zIndex: 50, width: 260,
              borderRadius: 10, overflow: "hidden"
            }}
            onMouseDown={(e) => e.stopPropagation()}
          >
            <div className={`px-3 py-2 ${darkMode ? "bg-secondary text-white" : "bg-light text-dark"} fw-semibold small`}>
              {isTable ? "Table Actions" : "Fixture Actions"}
            </div>

            {
              mode === "live" && !isTable ? <div className={`px-3 py-2 small ${darkMode ? "text-light" : "text-muted"}`}>No Available Options in Live Mode</div> :
                <div className="list-group list-group-flush">
                  {/* Status */}
                  {isTable && mode === "live" && (
                    <>
                      <div className={`px-3 py-2 small ${darkMode ? "text-light" : "text-muted"}`}>Status</div>
                      <div className="px-2 pb-2 d-grid gap-2">
                        {STATUS_LABEL.map(s => (
                          <button
                            key={s.key}
                            className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`}
                            onClick={async () => { 
                              setContextMenu(null); 
                              await changeStatus(el.id, s.key);
                             }}
                          >
                            {s.label}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                  {/* Edit */}
                    {isTable && (
                      <>
                          <div className={`px-3 py-2 small ${darkMode ? "text-light" : "text-muted"}`}>Edit</div>
                          <div className="px-2 pb-2 d-grid gap-2">
                          {
                            mode === "design" ? <button className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`} onClick={async () => { setContextMenu(null); await editSeatCount(el.id, el.seatCount ?? 4); }}>
                                  Change seat count…
                                </button>: ''
                          }
                          {
                            mode === "live" ? 
                            <>
                              {/* See ordered items */}
                              <button
                                 className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`}
                                onClick={async () => {
                                  await handleSeeOrderedItems(contextMenu.id);
                                  setContextMenu(null);
                                }}
                                // disabled={
                                //   !(contextMenu.element.sessionId || contextMenu.element.orderRef)
                                // }
                              >
                                See ordered items
                              </button>
                                {/* Show / Print QR */}
                              <button
                                 className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`}
                                  onClick={async () => {
                                    console.log(eMenus.map(e=>({value: e._id, label: e.name})))
                                    MySwal.fire({
                                      title: 'Select a Menu',
                                      input: 'select',
                                      inputOptions: eMenus.map(e=>({value: e._id, label: e.name})).reduce((acc, option) => {
                                        acc[option.value] = option.label;
                                        return acc;
                                      }, {}), // Pass the options object here
                                      inputPlaceholder: 'Select a Menu',
                                      showCancelButton: true,
                                      confirmButtonText: 'Confirm Selection',
                                      // preConfirm captures the input value before closing the modal
                                      preConfirm: (value) => {
                                        if (!value) {
                                          Swal.showValidationMessage('Please Select a Menu');
                                        }
                                        return value;
                                      },
                                    }).then((result) => {
                                      // The .then() block handles the confirmation result
                                      if (result.isConfirmed) {
                                        console.log(result);
                                        setSelectedMenu(result.value);
                                        (async() => await handleShowQR(contextMenu.id, result.value))();
                                      } else if (result.dismiss === Swal.DismissReason.cancel) {
                                        
                                      }
                                    });
                                  setContextMenu(null);
                                }}
                              >  Show / Print QR
                              </button>
                              <button className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`} onClick={async () => { setContextMenu(null); await setOrderRef(el.id, el.orderRef); }}>
                                Link Order / Ticket…
                              </button>
                              
                              </> : ''
                          }
                          {
                            mode === "design" ? <>
                              {/* Resize option */}
                              <button
                                className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`}
                                onClick={async () => {
                                  setContextMenu(null);
                                  await editSize(el.id, el.w, el.h);
                                }}
                              >
                                Resize…
                              </button>
                              <button className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`} onClick={async () => { setContextMenu(null); await editLabel(el.id, el.label); }}>
                                Edit label…
                              </button>
                            </> :"" }
                        </div>
                      </>
                    )}
                    {!isTable && (<>
                        <div className={`px-3 py-2 small ${darkMode ? "text-light" : "text-muted"}`}>Edit</div>
                        <div className="px-2 pb-2 d-grid gap-2">
                            <button className={`btn btn-sm ${darkMode ? "btn-outline-light" : "btn-outline-secondary"}`} onClick={async () => { setContextMenu(null); await editLabel(el.id, el.label); }}>
                              Edit label…
                            </button>
                        </div>
                      </>
                    )}
                  {
                    mode === "design" ? <>
                      {/* Danger */}
                      <div className={`px-3 py-2 small ${darkMode ? "text-light" : "text-muted"}`}>Danger</div>
                      <div className="px-2 pb-3 d-grid gap-2">
                        <button className="btn btn-sm btn-outline-danger" onClick={async () => { setContextMenu(null); await removeElement(el.id); }}>
                          Delete
                        </button>
                      </div> </> : ""
                  }
                </div>
            }
          </div>
        );
      })()}
    </div>
  );

  // ===== FEATURE DISABLED SCREEN (not in fullscreen path — default Swal is fine) =====
  if (!myStoreEnabled) {
    const enable = async () => {
      try {
        await Swal.fire({ // <--- Use default Swal here, as boardWrapRef is not active
          title: "Enable My Shop?",
          html:
            `<div class="text-start">
              <p class="mb-2">Do you run a dine-in or table-service business?</p>
              <p class="mb-0">Turn on <b>My Shop</b> to lay out tables, reserve them from the POS, and track status.</p>
            </div>`,
          showCancelButton: true,
          confirmButtonText: "Enable My Shop",
        }).then(async (r) => {
          if (!r.isConfirmed) return;
          await axios.patch(`/api/companies/${loadAuth("company")?.subdomain ?? "error"}`, { myStoreEnabled: 1 }, { headers: { "X-Tenant": subdomain } });
          await Swal.fire("Enabled", "My Shop is now active.", "success"); // <--- Use default Swal here
          let _c = loadAuth("company");
          _c.myStoreEnabled = true;
          saveAuthByKey("company",_c);
          window.location.reload();
        });
      } catch (e) {
        Swal.fire("Error", e?.response?.data?.error || e.message || "Failed to enable.", "error"); // <--- Use default Swal here
      }
    };

    return (
      <Wrapper title="My Shop">
        <div className="container py-5">
          <div className="mx-auto" style={{ maxWidth: 680 }}>
            <div className="card shadow-sm">
              <div className="card-body p-4">
                <h3 className="mb-2">You haven’t enabled this feature</h3>
                <p className="text-muted mb-4">
                  My Shop is for restaurants and table-service businesses. Enable it to place tables, reserve them from the POS, and track status.
                </p>
                <div className="d-flex gap-2">
                  <button className="btn btn-primary" onClick={enable}>Enable My Shop</button>
                  <a className="btn btn-outline-secondary" href="/dashboard">Go back</a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Wrapper>
    );
  }

  return (
    <Wrapper title="My Shop">
      <div className="container-fluid" ref={boardWrapRef} style={{ height: "calc(100vh - 120px)" }}>
        {/* Toolbar */}
        <div className="d-flex align-items-center gap-2 mb-3 px-2 py-3 my-2 bg-light">
          {mode === "design" && (
            <>
              <button className={`btn btn-sm btn-outline-secondary`} onClick={addTable}>
                <i className="fa fa-table me-1" /> Add Table
              </button>
              <button className={`btn btn-sm btn-outline-secondary`} onClick={addFixture}>
                <i className="fa fa-object-ungroup me-1" /> Add Fixture
              </button>
              <button className={`btn btn-sm btn-success`} onClick={saveLayout}><i className="fa fa-save me-1" /><Lucide.Save /> Save Design Changes</button>
            </>
          )}

          <div className="form-check form-switch ms-3">
            <input className="form-check-input" type="checkbox" id="snapSwitch" checked={snapToGrid} onChange={toggleSnap} />
            <label className="form-check-label" htmlFor="snapSwitch">Snap</label>
          </div>
          <div className="form-check form-switch ms-2">
            <input className="form-check-input" type="checkbox" id="fineSwitch" checked={(layout?.snapStep ?? Math.floor(cellSize/2)) !== cellSize} onChange={toggleFineSnap} />
            <label className="form-check-label" htmlFor="fineSwitch">Finer (½ cell)</label>
          </div>
          <div className="form-check form-switch ms-2">
            <input className="form-check-input" type="checkbox" id="gridSwitch" checked={showGrid} onChange={() => setShowGrid(!showGrid)} />
            <label className="form-check-label" htmlFor="gridSwitch">Grid</label>
          </div>

          <div className="form-check form-switch ms-2">
            <input className="form-check-input" type="checkbox" id="darkSwitch" checked={darkMode} onChange={() => setDarkMode(!darkMode)} />
            <label className="form-check-label" htmlFor="darkSwitch">Dark</label>
          </div>

          <div className="ms-auto d-flex align-items-center gap-3 small">
            <span className="d-inline-flex align-items-center">
              <span className="rounded-circle me-2 d-inline-block bg-success" style={{ width: 10, height: 10 }} /> Vacant
            </span>
            <span className="d-inline-flex align-items-center">
              <span className="rounded-circle me-2 d-inline-block bg-warning" style={{ width: 10, height: 10 }} /> Reserved
            </span>
            <span className="d-inline-flex align-items-center">
              <span className="rounded-circle me-2 d-inline-block bg-danger" style={{ width: 10, height: 10 }} /> Ordered / Verify
            </span>
            <span className="d-inline-flex align-items-center">
              <span className="rounded-circle me-2 d-inline-block bg-info" style={{ width: 10, height: 10 }} /> Preparing Order
            </span>
            <span className="d-inline-flex align-items-center">
              <span className="rounded-circle me-2 d-inline-block bg-primary" style={{ width: 10, height: 10 }} /> Ready to Serve
            </span>
            <span className="d-inline-flex align-items-center">
              <span className="rounded-circle me-2 d-inline-block bg-secondary" style={{ width: 10, height: 10 }} /> Served
            </span>

            <button className={`btn btn-sm btn-outline-secondary`} onClick={() => setMode(mode === "design" ? "live" : "design")}>
              {mode === "design" ? "Switch to Live" : "Switch to Design"}
            </button>
            <button className={`btn btn-sm btn-outline-secondary`} onClick={enterFullscreen}><i className="fa fa-expand me-1" /> Fullscreen</button>
            <button
              className="btn btn-sm btn-outline-danger"
              onClick={async () => {
                const ok = await swal({ title: "Disable My Shop?", text: "You can enable it again later.", showCancelButton: true, confirmButtonText: "Disable" });
                if (!ok.isConfirmed) return;
                await axios.patch(`/api/companies/${loadAuth("company")?.subdomain ?? "error"}`, { myStoreEnabled: 0 }, { headers: { "X-Tenant": subdomain } });
                await swal({ icon: "success", title: "Disabled", text: "My Shop was turned off." });
                let _c = loadAuth("company");
                _c.myStoreEnabled = false;
                saveAuthByKey("company",_c);
                window.location.reload();
              }}
            >
              Disable
            </button>
          </div>
        </div>

        <div className={`border rounded overflow-hidden ${darkMode ? "border-secondary" : ""}`} style={{ height: "100%" }}>
          <GridBoard />
        </div>
      </div>
    </Wrapper>
  );
}